import { i18n } from '@fs/zion-locale'
import * as Yup from 'yup'
import { getDuplicatedRelationshipEvents } from './relationship.utils'
import { getRelationshipsBetweenTwoPeopleByType } from './temporary.utils'
import { setNestedError } from '../../../../utils/formResolvers.utils'

/**
 * Yup validation schema for event form.
 *
 * Fields:
 * - eventType: Validates as an object, can be null. If all primaryEventTypes are falsy,
 *   this field becomes required. This applies to ALL persons regardless of person.primary flag.
 * - otherPerson: Conditionally required based on eventType. If eventType indicates a relationship event,
 *   this field is required; otherwise, it is nullable. Additionally, it includes a custom test to ensure that
 *   if the relationship type is 'COUPLE', the selected otherPerson does not already have a 'PARENT_CHILD' relationship with the main person.
 *
 * @constant
 * @type {Yup.ObjectSchema}
 */
const eventSchemaShape = Yup.object().shape({
  eventType: Yup.object()
    .nullable()
    .test(
      'primary-event-required',
      i18n.t('eventsCard.validation.primaryEventRequired', 'At least one primary event is required.'),
      // eslint-disable-next-line func-names
      function validatePrimaryEventType(value) {
        const currentPath = this.path

        // Only apply this validation to primary events, not additional events
        if (!currentPath?.includes('events.primary')) {
          return true
        }

        // Use this.options.from to get form values passed from React Hook Form
        // This ensures we're validating against the CURRENT form state, not stale context
        const formValues = this.options.from?.[3]?.value || {}
        const primaryEvents = formValues?.events?.primary || []

        // Extract the current field's index from the path (e.g., "events.primary[0].eventType" -> 0)
        const match = /events\.primary\[(\d+)\]\.eventType/.exec(currentPath)
        const currentIndex = match ? Number.parseInt(match[1], 10) : -1

        // Check if all OTHER primary events have no eventType
        const otherPrimaryEventTypes = primaryEvents.filter((_, idx) => idx !== currentIndex).map((ev) => ev.eventType)

        // If there are other primary events and all of them have no type, THIS field is required
        if (otherPrimaryEventTypes.length > 0 && otherPrimaryEventTypes.every((ev) => !ev)) {
          return !!value
        }

        // If this is the only primary event, it's required
        if (primaryEvents.length === 1) {
          return !!value
        }

        return true
      }
    ),
  otherPerson: Yup.mixed()
    .when('eventType', {
      is: (eventType) => eventType?.isRelationshipEvent,
      then: (schema) => schema.required(i18n.t('eventsCard.validation.notEmpty', 'Field cannot be empty.')),
      otherwise: (schema) => schema.nullable(),
    })
    .test(
      'no-couple-if-parent-child',
      i18n.t('eventsCard.validation.noCoupleIfParentChild', "Name can't be both a child and a spouse."),
      (value, testContext) => {
        const { eventType } = testContext.parent
        const person = testContext.options?.context?.person
        const relType = eventType?.relType
        const otherPersonId = value?.key

        if (relType === 'COUPLE' && otherPersonId && person) {
          const hasParentChild =
            getRelationshipsBetweenTwoPeopleByType({
              person,
              otherPersonId,
              relType: 'PARENT_CHILD',
            })?.length > 0

          if (hasParentChild) {
            return false
          }
        }
        return true
      }
    )
    .test(
      'only-same-record-person',
      i18n.t(
        'eventsCard.validation.relationshipPersonSameRecord',
        'The selected person must belong to the same record as the main person.'
      ),
      (value, testContext) => {
        if (!value) return true

        const otherPersonRecord = value?.recordId
        const personRecordId = testContext.options?.context?.person?.recordId
        if (otherPersonRecord && personRecordId) {
          return otherPersonRecord === personRecordId
        }

        // If either recordId is missing, we can't validate, so we return false
        return false
      }
    ),
})

/**
 * Factory function to create date/place field validation schemas.
 *
 * @param {Object} config - Configuration object
 * @param {'DATE'|'PLACE'} config.fieldType - The type of field to validate
 * @param {boolean} config.isPrimary - Whether this is for primary events (always required) or additional events (conditionally required)
 * @returns {Yup.ObjectSchema} The validation schema for the field
 */
const createFieldSchema = ({ fieldType, isPrimary }) => {
  const testName = isPrimary
    ? `primary-${fieldType.toLowerCase()}-required`
    : `additional-${fieldType.toLowerCase()}-required-if-event-type`

  const errorMessage = i18n.t(
    `eventsCard.validation.primary${fieldType === 'DATE' ? 'Date' : 'Place'}Required`,
    `${fieldType === 'DATE' ? 'Date' : 'Place'} is required.`
  )

  return Yup.object().shape({
    subElements: Yup.array().of(
      Yup.object().shape({
        formValue: Yup.object().test(testName, errorMessage, function validateField(value, testContext) {
          if (testContext.parent.fieldType !== fieldType) {
            return true
          }

          // For primary events, the field is always required
          if (isPrimary) {
            return Boolean(value.originalText || value.standard?.standardizedText)
          }

          // For additional events, only require if eventType is selected
          // Path example: "events.additional[0].dates[0].subElements[0].formValue"
          // Traverse up the validation context hierarchy to get the event object
          // from[0] = formValue (current value)
          // from[1] = subElement object
          // from[2] = dates[0] or places[0] array item
          // from[3] = additional[0] (the event object we need!)
          const currentEvent = testContext.from?.[3]?.value

          // If eventType is selected, field is required
          if (currentEvent?.eventType) {
            return Boolean(value.originalText || value.standard?.standardizedText)
          }

          return true
        }),
      })
    ),
  })
}

// Create schemas using the factory
const primaryDatesSchema = createFieldSchema({ fieldType: 'DATE', isPrimary: true })
const primaryPlacesSchema = createFieldSchema({ fieldType: 'PLACE', isPrimary: true })
const additionalDatesSchema = createFieldSchema({ fieldType: 'DATE', isPrimary: false })
const additionalPlacesSchema = createFieldSchema({ fieldType: 'PLACE', isPrimary: false })

const primaryEventSchemaShape = Yup.object().shape({
  ...eventSchemaShape.fields,
  dates: Yup.array().of(primaryDatesSchema),
  places: Yup.array().of(primaryPlacesSchema),
})

const additionalEventSchemaShape = Yup.object().shape({
  ...eventSchemaShape.fields,
  dates: Yup.array().of(additionalDatesSchema),
  places: Yup.array().of(additionalPlacesSchema),
})

/**
 * Yup validation schema for validating an object containing event arrays.
 *
 * The schema expects an object with an `events` property, which itself contains:
 * - `primary`: an array of objects validated by `eventSchema`
 * - `additional`: an array of objects validated by `eventSchema`
 *
 * Includes a custom test 'collect-primary-event-types' that collects the `eventType` of each primary event
 * and assigns the array to `context.primaryEventTypes` for validation of required primary events.
 *
 * @constant
 * @type {Yup.ObjectSchema}
 * @property {Object} events - The events object to validate.
 * @property {Array<Object>} events.primary - Array of primary event objects validated by `eventSchema`.
 * @property {Array<Object>} events.additional - Array of additional event objects validated by `eventSchema`.
 */
const basicEventsValidationSchema = Yup.object()
  .shape({
    events: Yup.object().shape({
      primary: Yup.array().of(primaryEventSchemaShape),
      additional: Yup.array().of(additionalEventSchemaShape),
    }),
  })
  .test('collect-primary-event-types', '', (value, testContext) => {
    const primaryEventTypes = value?.events?.primary?.map((ev) => ev.eventType)
    if (testContext.options.context) {
      testContext.options.context.primaryEventTypes = primaryEventTypes
    }
    return true
  })

/**
 * Validates the event form values using Yup and custom duplicate relationship logic.
 *
 * @async
 * @param {Object} formValues - The values from the event form to validate.
 * @param {Object} testContext - Additional context for validation, including the current person.
 * @param {Object} testContext.person - The person object used for duplicate relationship checks.
 * @returns {Promise<{values: Object, errors: Object}>} An object containing validated values (or empty object if errors) and a map of validation errors.
 */
export const eventsResolver = async (formValues, testContext) => {
  const errors = {}

  // Validate with Yup first (for all other simple errors)
  try {
    await basicEventsValidationSchema.validate(formValues, { abortEarly: false, context: testContext })
  } catch (yupError) {
    if (yupError.inner) {
      yupError.inner.forEach((err) => {
        if (err.path) {
          setNestedError(errors, err.path, err.message)
        }
      })
    }
  }

  // Now check for duplicate relationship events
  const primaryEventsLength = formValues?.events?.primary?.length || 0
  const { person } = testContext || {}
  const { duplicates, relationshipCombos } = getDuplicatedRelationshipEvents(formValues, person)
  const takenRelationshipIds = new Set()

  if (duplicates?.length > 0) {
    // Add an error for each duplicate
    duplicates.forEach((duplicate) => {
      const { position, isFromPrimaryList, combo, type } = duplicate
      const arrName = isFromPrimaryList ? 'primary' : 'additional'
      const arrIdx = isFromPrimaryList ? position : position - primaryEventsLength
      const basePath = `events.${arrName}[${arrIdx}]`
      const relationshipId = relationshipCombos.find((relCombo) => relCombo.combo === combo)?.relationshipId

      // If the relationship does not have any events yet, we can add the current event to it.
      if (takenRelationshipIds.has(relationshipId) || type === 'EVENT') {
        const path = `${basePath}.otherPerson`
        setNestedError(
          errors,
          path,
          i18n.t(
            'eventsCard.validation.duplicateRelationship',
            'This relationship already exists between the selected persons.'
          )
        )
      } else {
        takenRelationshipIds.add(relationshipId)
      }
    })
  }

  return {
    values: Object.keys(errors).length === 0 ? formValues : {},
    errors,
  }
}
